import type { Entity } from '@/types';
import type { Repository } from './createRepository';

export class PlanLimitError extends Error {
  constructor(readonly entity: string, readonly limit: number) {
    super(`Free plan is limited to ${limit} saved ${entity}.`);
    this.name = 'PlanLimitError';
  }
}

export function isPlanLimitError(error: unknown): error is PlanLimitError {
  return error instanceof PlanLimitError;
}

/**
 * Wraps a repository so plan limits are enforced at the persistence boundary.
 * The UI cannot bypass the cap by calling a different code path, and the
 * upgrade prompt is driven by a typed error rather than a scattered `if`.
 */
export function withLimit<T extends Entity>(
  repository: Repository<T>,
  getLimit: () => number | null,
  entityLabel: string,
): Repository<T> {
  const assertCapacity = async (incoming: number) => {
    const limit = getLimit();
    if (limit === null) return;
    const existing = await repository.list();
    if (existing.length + incoming > limit) throw new PlanLimitError(entityLabel, limit);
  };

  return {
    ...repository,
    async create(input) {
      await assertCapacity(1);
      return repository.create(input);
    },
    async createMany(inputs) {
      await assertCapacity(inputs.length);
      return repository.createMany(inputs);
    },
  };
}
